import { useState, useEffect, useCallback } from "react";
import { useLocation } from "react-router-dom";
import {
  Megaphone,
  Plus,
  Trash2,
} from "lucide-react";
import Pagination from "../../../components/ui/Pagination";
import ConfirmationModal from "../../../components/ui/ConfirmationModal";
import PageHeader from "../../../components/ui/PageHeader";
import LoadingSpinner from "../../../components/ui/LoadingSpinner";
import SendAnnouncementModal from "./SendAnnouncementModal";
import AnnouncementCard from "./AnnouncementCard";
import EmptyState from "./EmptyState";
import {
  GET_ANNOUNCEMENTS_URL,
  ADD_ANNOUNCEMENT_URL,
  UPDATE_ANNOUNCEMENT_URL,
  DELETE_ANNOUNCEMENT_URL,
} from "../../../api/url";
import { apiCall } from "../../../api/fetchClient";

const AnnouncementPage = () => {
  const location = useLocation();
  const [announcements, setAnnouncements] = useState([]);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editingAnnouncement, setEditingAnnouncement] = useState(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(10);
  const [loading, setLoading] = useState(false);

  // Deletion modals
  const [confirmModal, setConfirmModal] = useState({
    isOpen: false,
    isDeleteAll: false,
    announcementToDelete: null,
  });

  const [pagination, setPagination] = useState({
    totalPages: 1,
    totalAnnouncements: 0,
    hasNextPage: false,
    hasPrevPage: false,
  });

  const fetchAnnouncements = useCallback(async () => {
    try {
      setLoading(true);

      const params = new URLSearchParams({
        page: currentPage.toString(),
        limit: itemsPerPage.toString(),
      });

      const response = await apiCall(
        `${GET_ANNOUNCEMENTS_URL}?${params.toString()}`,
        "GET"
      );

      if (response && response.success) {
        const rawList = response.results?.announcements || [];

        const merged = rawList.map((item) => ({
          ...item,
          image: item.image || item.imageUrl || null,
          attachment: item.attachment || null,
        }));

        setAnnouncements(merged);
        setPagination({
          totalPages: response.results.pagination?.totalPages || 1,
          totalAnnouncements: response.results.pagination?.totalItems || merged.length,
          hasNextPage: response.results.pagination?.hasNextPage || false,
          hasPrevPage: response.results.pagination?.hasPrevPage || false,
        });
      } else {
        setAnnouncements([]);
      }
    } catch (error) {
      setAnnouncements([]);
    } finally {
      setLoading(false);
    }
  }, [currentPage, itemsPerPage]);

  useEffect(() => {
    fetchAnnouncements();
  }, [fetchAnnouncements]);

  useEffect(() => {
    setCurrentPage(1);
  }, [itemsPerPage]);

  useEffect(() => {
    if (location.state?.openAddModal) {
      setIsModalOpen(true);
      window.history.replaceState({}, document.title);
    }
  }, [location]);

  // Clear any legacy client-cached announcement images/attachments from localStorage
  useEffect(() => {
    try {
      localStorage.removeItem("peoplesync_announcement_images");
      localStorage.removeItem("peoplesync_announcement_attachments");
    } catch {
      // Ignored
    }
  }, []);

  const handleEditItem = (announcement) => {
    setEditingAnnouncement(announcement);
    setIsModalOpen(true);
  };

  const handleSaveAnnouncement = async (announcementData) => {
    try {
      const isEditing = Boolean(announcementData.id);
      const scope = announcementData.scope || "all_users";

      let response;
      if (announcementData.rawFile) {
        // Send as multipart/form-data with 'file' field
        const formData = new FormData();
        formData.append("title", announcementData.title);
        formData.append("message", announcementData.message);
        formData.append("scope", scope);
        formData.append("type", announcementData.type || "info");
        if (announcementData.companyId) {
          formData.append("companyId", announcementData.companyId);
        }
        if (announcementData.targetValues && announcementData.targetValues.length > 0) {
          formData.append(
            "targetValues",
            JSON.stringify(announcementData.targetValues)
          );
        }
        formData.append("file", announcementData.rawFile);

        if (isEditing) {
          formData.append("id", announcementData.id);
          response = await apiCall(
            `${UPDATE_ANNOUNCEMENT_URL}?id=${announcementData.id}`,
            "PUT",
            formData,
            true
          );
        } else {
          response = await apiCall(
            ADD_ANNOUNCEMENT_URL,
            "POST",
            formData,
            true
          );
        }
      } else {
        // Send as application/json when no new file is attached
        const apiBody = {
          title: announcementData.title,
          message: announcementData.message,
          scope,
          type: announcementData.type || "info",
        };
        if (announcementData.companyId) {
          apiBody.companyId = announcementData.companyId;
        }
        if (announcementData.targetValues && announcementData.targetValues.length > 0) {
          apiBody.targetValues = announcementData.targetValues;
        }

        if (isEditing) {
          response = await apiCall(
            `${UPDATE_ANNOUNCEMENT_URL}?id=${announcementData.id}`,
            "PUT",
            { ...apiBody, id: announcementData.id },
            false
          );
        } else {
          response = await apiCall(ADD_ANNOUNCEMENT_URL, "POST", apiBody, false);
        }
      }

      if (response && response.success) {
        fetchAnnouncements();
        setIsModalOpen(false);
        setEditingAnnouncement(null);
      }
    } catch (error) {
      // Handled in apiCall
    }
  };

  const handleDeleteItem = (announcement) => {
    setConfirmModal({
      isOpen: true,
      isDeleteAll: false,
      announcementToDelete: announcement,
    });
  };

  const handleConfirmDelete = async () => {
    try {
      if (confirmModal.isDeleteAll) {
        const response = await apiCall(DELETE_ANNOUNCEMENT_URL, "DELETE");
        if (response && response.success) {
          fetchAnnouncements();
        }
      } else if (confirmModal.announcementToDelete) {
        const targetId =
          confirmModal.announcementToDelete._id ||
          confirmModal.announcementToDelete.id;

        const response = await apiCall(
          `${DELETE_ANNOUNCEMENT_URL}?id=${targetId}`,
          "DELETE"
        );

        if (response && response.success) {
          fetchAnnouncements();
        }
      }
    } catch (error) {
      // Error handled in apiCall
    } finally {
      setConfirmModal({
        isOpen: false,
        isDeleteAll: false,
        announcementToDelete: null,
      });
    }
  };

  return (
    <div className="min-h-screen pb-16">
      <PageHeader
        title="Announcements"
        description="Stay updated with what's happening in your organization."
        icon={Megaphone}
      >
        {announcements.length > 0 && (
          <button
            type="button"
            onClick={() =>
              setConfirmModal({
                isOpen: true,
                isDeleteAll: true,
                announcementToDelete: null,
              })
            }
            className="inline-flex items-center gap-1.5 px-3.5 py-2.5 text-sm font-medium text-red-600 hover:text-red-700 dark:text-red-400 bg-red-50 hover:bg-red-100 dark:bg-red-950/30 dark:hover:bg-red-900/40 border border-red-200 dark:border-red-900/50 rounded-xl transition-all"
          >
            <Trash2 className="w-4 h-4" />
            <span className="hidden sm:inline">Delete All</span>
          </button>
        )}

        <button
          type="button"
          onClick={() => {
            setEditingAnnouncement(null);
            setIsModalOpen(true);
          }}
          className="inline-flex items-center gap-2 px-4 py-2.5 text-sm font-semibold text-white bg-[#05DAC3] hover:bg-[#00B4A6] rounded-xl transition-all shadow-sm hover:shadow-md active:scale-95"
        >
          <Plus className="w-4 h-4" />
          <span>Send Announcement</span>
        </button>
      </PageHeader>

      {/* Main Feed Content */}
      <div className="max-w-3xl mx-auto">
        {loading ? (
          <LoadingSpinner message="Loading announcements..." minHeight="h-64" />
        ) : announcements.length > 0 ? (
          <div className="space-y-4 mb-6">
            {announcements.map((announcement) => (
              <AnnouncementCard
                key={announcement._id || announcement.id}
                announcement={announcement}
                onEdit={handleEditItem}
                onDelete={handleDeleteItem}
              />
            ))}
          </div>
        ) : (
          <div className="bg-white dark:bg-gray-800 rounded-2xl border border-gray-200 dark:border-gray-700/80 p-8 shadow-xs">
            <EmptyState
              onAddAnnouncement={() => {
                setEditingAnnouncement(null);
                setIsModalOpen(true);
              }}
            />
          </div>
        )}

        {/* Pagination */}
        {pagination.totalAnnouncements > itemsPerPage && (
          <div className="mt-6">
            <Pagination
              currentPage={currentPage}
              totalPages={pagination.totalPages}
              onPageChange={setCurrentPage}
              totalItems={pagination.totalAnnouncements}
              itemsPerPage={itemsPerPage}
              onItemsPerPageChange={(newLimit) => {
                setItemsPerPage(newLimit);
                setCurrentPage(1);
              }}
              itemsPerPageOptions={[10, 20, 50, 100]}
            />
          </div>
        )}
      </div>

      {/* Send / Edit Announcement Modal */}
      <SendAnnouncementModal
        isOpen={isModalOpen}
        initialData={editingAnnouncement}
        onClose={() => {
          setIsModalOpen(false);
          setEditingAnnouncement(null);
        }}
        onSave={handleSaveAnnouncement}
      />

      {/* Confirmation Modal */}
      <ConfirmationModal
        isOpen={confirmModal.isOpen}
        onClose={() =>
          setConfirmModal({
            isOpen: false,
            isDeleteAll: false,
            announcementToDelete: null,
          })
        }
        onConfirm={handleConfirmDelete}
        title={
          confirmModal.isDeleteAll
            ? "Delete All Announcements"
            : "Delete Announcement"
        }
        message={
          confirmModal.isDeleteAll
            ? "Are you sure you want to delete all announcements across the organization? This action cannot be undone."
            : `Are you sure you want to delete "${confirmModal.announcementToDelete?.title || "this announcement"}"?`
        }
        confirmText={confirmModal.isDeleteAll ? "Delete All" : "Delete"}
        variant="danger"
        icon={Trash2}
      />
    </div>
  );
};

export default AnnouncementPage;